import {
	type OrderDetailResponseDto,
	type OrderUpdateDto,
	patchOrdersId,
} from "Infrastructure/Api/Api";
import { processResponse } from "Infrastructure/Api/ApiUtils";
import { Resources, useResource } from "Infrastructure/Translation/Resources";
import {
	invalidateOrderDetailBundle,
	invalidatePartnerCalendar,
} from "Order/Api/invalidateOrderQueries";
import { orderKeys } from "Order/Api/orderKeys";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";

const orderMutationsResource = Resources.base.order.mutations;
type UpdateOrderInput = Partial<OrderUpdateDto> & {
	jobDurationDays?: number | null;
	unfinishOrder?: boolean;
};

export const useUpdateOrder = (orderId: number) => {
	const { t } = useResource();
	const queryClient = useQueryClient();

	return useMutation<OrderDetailResponseDto, Error, UpdateOrderInput>({
		mutationFn: processResponse((data: UpdateOrderInput) =>
			patchOrdersId(
				{ ...data, incrementCount: data.incrementCount ?? false },
				orderId,
			),
		),
		onError: (error) => {
			toast.error(error.message);
			console.error(error);
		},
		onSuccess: async (_data, variables) => {
			toast.success(t(orderMutationsResource.orderUpdateSuccess));
			await Promise.all([
				invalidateOrderDetailBundle(queryClient, orderId),
				queryClient.invalidateQueries({ queryKey: orderKeys.listAll }),
				...(variables.jobDurationDays !== undefined
					? [invalidatePartnerCalendar(queryClient)]
					: []),
			]);
		},
	});
};
